import React, { useState, useEffect, useCallback } from 'react';
import { playbookApi } from '../../api/playbookApi';
import type { AIGeneratedPlaybook, TaskTemplateSummary } from '../../types/playbook.types';
import { apiClient } from '@/config/api';

interface MigrationWizardProps {
  onComplete: (playbookId: string) => void;
  onClose: () => void;
}

type WizardStep = 'select' | 'configure' | 'preview' | 'saving';

const MigrationWizard: React.FC<MigrationWizardProps> = ({ onComplete, onClose }) => {
  const [step, setStep] = useState<WizardStep>('select');
  const [templates, setTemplates] = useState<TaskTemplateSummary[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplateSummary | null>(null);
  const [enhanceWithAI, setEnhanceWithAI] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<AIGeneratedPlaybook | null>(null);

  // Load templates
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingTemplates(true);
      try {
        const resp = await apiClient.get<any>('/api/v1/task-templates');
        const data = (resp as any)?.data ?? resp;
        const list = Array.isArray(data) ? data : (data?.templates ?? data?.data ?? []);
        if (!cancelled) setTemplates(list);
      } catch {
        if (!cancelled) setTemplates([]);
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredTemplates = templates.filter((t) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return t.name?.toLowerCase().includes(s) || t.practice_area?.toLowerCase().includes(s);
  });

  const handleSelectTemplate = useCallback((t: TaskTemplateSummary) => {
    setSelectedTemplate(t);
    setStep('configure');
  }, []);

  const handleMigrate = useCallback(async () => {
    if (!selectedTemplate) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const resp = await playbookApi.migrateTemplate({
        templateId: selectedTemplate.id,
        enhanceWithAI,
      });
      const data = (resp as any)?.playbook ?? (resp as any)?.data?.playbook;
      if (data) {
        setPreview(data);
        setStep('preview');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Migration failed');
    } finally {
      setLoading(false);
    }
  }, [selectedTemplate, enhanceWithAI]);

  const handlePersist = useCallback(async () => {
    if (!preview) return;
    setStep('saving');
    setError(null);
    try {
      const resp = await playbookApi.persistGeneratedPlaybook(preview);
      const saved = (resp as any)?.playbook ?? (resp as any)?.data?.playbook;
      const newId = saved?.id;
      if (newId) {
        onComplete(newId);
      } else {
        onComplete('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save playbook');
      setStep('preview');
    }
  }, [preview, onComplete]);

  const inputClass = 'w-full rounded-md border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-[520px] max-h-[80vh] rounded-xl bg-white dark:bg-gray-800 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'fadeSlideIn 200ms ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Migrate Task Template to Playbook</h3>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
              {step === 'select' && 'Step 1: Select a template'}
              {step === 'configure' && 'Step 2: Configure migration'}
              {step === 'preview' && 'Step 3: Review generated playbook'}
              {step === 'saving' && 'Saving...'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg">×</button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-1 px-5 py-2 border-b border-gray-100 dark:border-gray-700">
          {(['select', 'configure', 'preview'] as const).map((s, i) => (
            <React.Fragment key={s}>
              <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-bold ${
                step === s ? 'bg-violet-600 text-white' :
                (['select', 'configure', 'preview'].indexOf(step) > i) ? 'bg-emerald-500 text-white' :
                'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
              }`}>
                {(['select', 'configure', 'preview'].indexOf(step) > i) ? '✓' : i + 1}
              </div>
              {i < 2 && <div className={`flex-1 h-0.5 ${(['select', 'configure', 'preview'].indexOf(step) > i) ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-gray-700'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="mx-5 mt-2 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[9px] text-rose-600 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-400">
            {error}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Step 1: Select */}
          {step === 'select' && (
            <div className="space-y-2">
              <input
                type="text"
                className={inputClass}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search templates..."
              />
              {loadingTemplates && (
                <div className="flex items-center justify-center py-8">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-violet-600 border-t-transparent" />
                </div>
              )}
              {!loadingTemplates && filteredTemplates.length === 0 && (
                <p className="text-[10px] text-gray-400 text-center py-8">No templates found</p>
              )}
              {filteredTemplates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => handleSelectTemplate(t)}
                  className="w-full text-left rounded-lg border border-gray-200 dark:border-gray-700 p-3 hover:border-violet-300 dark:hover:border-violet-600 transition-colors"
                >
                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{t.name}</div>
                  {t.description && (
                    <div className="text-[9px] text-gray-400 dark:text-gray-500 mt-0.5 truncate">{t.description}</div>
                  )}
                  {t.practice_area && (
                    <span className="inline-block mt-1 rounded-full bg-lex-accent-soft text-lex-accent px-1.5 py-0.5 text-[8px]">
                      {t.practice_area}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Configure */}
          {step === 'configure' && selectedTemplate && (
            <div className="space-y-4">
              <div className="rounded-lg border border-violet-200 dark:border-violet-700 bg-violet-50/30 dark:bg-violet-900/10 p-3">
                <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{selectedTemplate.name}</div>
                {selectedTemplate.practice_area && (
                  <span className="inline-block mt-1 rounded-full bg-lex-accent-soft text-lex-accent px-1.5 py-0.5 text-[8px]">
                    {selectedTemplate.practice_area}
                  </span>
                )}
              </div>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enhanceWithAI}
                  onChange={(e) => setEnhanceWithAI(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 dark:border-gray-600"
                />
                <div>
                  <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">Enhance with AI</span>
                  <p className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5">
                    AI will suggest guards, validators, and actions based on the practice area and template structure.
                  </p>
                </div>
              </label>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setSelectedTemplate(null); setStep('select'); }}
                  className="flex-1 rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleMigrate}
                  disabled={loading}
                  className="flex-1 rounded bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  {loading && <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />}
                  {loading ? 'Migrating...' : 'Migrate'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && preview && (
            <div className="space-y-3">
              <div className="rounded-lg border border-violet-200 dark:border-violet-700 bg-violet-50/30 dark:bg-violet-900/10 p-3 space-y-1.5">
                <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{preview.name}</div>
                <div className="text-[9px] text-gray-500 dark:text-gray-400">{preview.description}</div>
                <div className="flex gap-2 text-[9px]">
                  <span className="text-emerald-600">{preview.statuses.length} statuses</span>
                  <span className="text-lex-accent">{preview.transitions.length} transitions</span>
                  <span className="text-violet-600">{preview.practiceArea}</span>
                </div>
                <div className="text-[8px] text-gray-400">
                  Confidence: {Math.round(preview.metadata.confidence * 100)}% | Model: {preview.metadata.model}
                </div>
              </div>

              {/* Status flow preview */}
              <div className="space-y-1">
                <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Statuses</span>
                <div className="flex flex-wrap gap-1">
                  {preview.statuses.map((s) => (
                    <span key={s.slug} className={`rounded-full px-2 py-0.5 text-[8px] font-medium border ${
                      s.category === 'done' ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300' :
                      s.category === 'in_progress' ? 'border-lex-accent bg-lex-accent-soft text-lex-accent' :
                      s.category === 'blocked' ? 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-300' :
                      'border-gray-300 bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300'
                    }`}>
                      {s.name}
                    </span>
                  ))}
                </div>
              </div>

              {/* Transitions */}
              <div className="space-y-1">
                <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Transitions</span>
                {preview.transitions.map((t) => (
                  <div key={t.slug} className="flex items-center gap-1 text-[9px] text-gray-600 dark:text-gray-400">
                    <span>{t.from_status_slug}</span>
                    <span className="text-gray-300 dark:text-gray-600">→</span>
                    <span>{t.to_status_slug}</span>
                    {t.guards.length > 0 && <span className="text-amber-500 ml-1">{t.guards.length}G</span>}
                    {t.validators.length > 0 && <span className="text-lex-accent">{t.validators.length}V</span>}
                    {t.actions.length > 0 && <span className="text-emerald-500">{t.actions.length}A</span>}
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => { setPreview(null); setStep('configure'); }}
                  className="flex-1 rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handlePersist}
                  className="flex-1 rounded bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 flex items-center justify-center gap-1.5"
                >
                  Save as New Playbook
                </button>
              </div>
            </div>
          )}

          {/* Step: Saving */}
          {step === 'saving' && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-violet-600 border-t-transparent" />
              <span className="text-xs text-gray-500 dark:text-gray-400">Saving playbook...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MigrationWizard;
